// Chrome extension storage API utilities

// In-memory cache for frequently accessed values
const storageCache = {
  jwt_token: undefined,
  user_id: undefined,
  status: undefined,
  last_used_group: undefined,
  user_config: undefined,
  _initialized: false,
};

// Initialize cache from storage on first access
async function initializeCache() {
  if (storageCache._initialized) return;
  if (typeof globalThis.chrome === 'undefined' || !globalThis.chrome.storage) {
    storageCache._initialized = true;
    return;
  }

  return new Promise(resolve => {
    globalThis.chrome.storage.local.get(
      ['jwt_token', 'user_id', 'status', 'last_used_group', 'user_config'],
      result => {
        storageCache.jwt_token = result.jwt_token || null;
        storageCache.user_id = result.user_id || null;
        storageCache.status = result.status || null;
        storageCache.last_used_group = result.last_used_group || null;
        storageCache.user_config = result.user_config || null;
        storageCache._initialized = true;
        resolve();
      }
    );
  });
}

export class ExtensionStorage {
  // Get token from cache or storage
  static async getToken() {
    await initializeCache();
    if (storageCache.jwt_token !== undefined) {
      return storageCache.jwt_token;
    }

    return new Promise(resolve => {
      if (
        typeof globalThis.chrome === 'undefined' ||
        !globalThis.chrome.storage
      ) {
        resolve(null);
        return;
      }

      globalThis.chrome.storage.local.get(['jwt_token'], result => {
        storageCache.jwt_token = result.jwt_token || null;
        resolve(storageCache.jwt_token);
      });
    });
  }

  // Set token in globalThis.chrome.storage
  static async setToken(token) {
    storageCache.jwt_token = token;
    return new Promise(resolve => {
      if (
        typeof globalThis.chrome === 'undefined' ||
        !globalThis.chrome.storage
      ) {
        resolve(false);
        return;
      }

      globalThis.chrome.storage.local.set({ jwt_token: token }, () => {
        resolve(!globalThis.chrome.runtime.lastError);
      });
    });
  }

  // Get user ID
  static async getUserId() {
    await initializeCache();
    if (storageCache.user_id !== undefined) {
      return storageCache.user_id;
    }

    return new Promise(resolve => {
      if (
        typeof globalThis.chrome === 'undefined' ||
        !globalThis.chrome.storage
      ) {
        resolve(null);
        return;
      }

      globalThis.chrome.storage.local.get(['user_id'], result => {
        storageCache.user_id = result.user_id || null;
        resolve(storageCache.user_id);
      });
    });
  }

  // Set user ID
  static async setUserId(userId) {
    storageCache.user_id = userId;
    return new Promise(resolve => {
      if (
        typeof globalThis.chrome === 'undefined' ||
        !globalThis.chrome.storage
      ) {
        resolve(false);
        return;
      }

      globalThis.chrome.storage.local.set({ user_id: userId }, () => {
        resolve(!globalThis.chrome.runtime.lastError);
      });
    });
  }

  // Get status
  static async getStatus() {
    await initializeCache();
    if (storageCache.status !== undefined) {
      return storageCache.status;
    }

    return new Promise(resolve => {
      if (
        typeof globalThis.chrome === 'undefined' ||
        !globalThis.chrome.storage
      ) {
        resolve(null);
        return;
      }

      globalThis.chrome.storage.local.get(['status'], result => {
        storageCache.status = result.status || null;
        resolve(storageCache.status);
      });
    });
  }

  // Set status
  static async setStatus(status) {
    storageCache.status = status;
    return new Promise(resolve => {
      if (
        typeof globalThis.chrome === 'undefined' ||
        !globalThis.chrome.storage
      ) {
        resolve(false);
        return;
      }

      globalThis.chrome.storage.local.set({ status: status }, () => {
        resolve(!globalThis.chrome.runtime.lastError);
      });
    });
  }

  // Get last used group ID
  static async getLastUsedGroupId() {
    await initializeCache();
    if (storageCache.last_used_group !== undefined) {
      return storageCache.last_used_group;
    }

    return new Promise(resolve => {
      if (
        typeof globalThis.chrome === 'undefined' ||
        !globalThis.chrome.storage
      ) {
        resolve(null);
        return;
      }

      globalThis.chrome.storage.local.get(['last_used_group'], result => {
        storageCache.last_used_group = result.last_used_group || null;
        resolve(storageCache.last_used_group);
      });
    });
  }

  // Set last used group ID
  static async setLastUsedGroupId(groupId) {
    storageCache.last_used_group = groupId;
    return new Promise(resolve => {
      if (
        typeof globalThis.chrome === 'undefined' ||
        !globalThis.chrome.storage
      ) {
        resolve(false);
        return;
      }

      globalThis.chrome.storage.local.set({ last_used_group: groupId }, () => {
        resolve(!globalThis.chrome.runtime.lastError);
      });
    });
  }

  // Get user configuration
  static async getConfiguration() {
    await initializeCache();
    if (storageCache.user_config !== undefined) {
      try {
        return storageCache.user_config
          ? JSON.parse(storageCache.user_config)
          : null;
      } catch {
        return null;
      }
    }

    return new Promise(resolve => {
      if (
        typeof globalThis.chrome === 'undefined' ||
        !globalThis.chrome.storage
      ) {
        resolve(null);
        return;
      }

      globalThis.chrome.storage.local.get(['user_config'], result => {
        try {
          storageCache.user_config = result.user_config;
          const config = result.user_config;
          resolve(config ? JSON.parse(config) : null);
        } catch (error) {
          console.error('Error parsing user configuration:', error);
          resolve(null);
        }
      });
    });
  }

  // Set user configuration
  static async setConfiguration(config) {
    const configString = config ? JSON.stringify(config) : null;
    storageCache.user_config = configString;
    return new Promise(resolve => {
      if (
        typeof globalThis.chrome === 'undefined' ||
        !globalThis.chrome.storage
      ) {
        resolve(false);
        return;
      }

      globalThis.chrome.storage.local.set({ user_config: configString }, () => {
        resolve(!globalThis.chrome.runtime.lastError);
      });
    });
  }

  // Check if the mobile app banner has been dismissed
  static async getMobileBannerDismissed() {
    return new Promise(resolve => {
      if (
        typeof globalThis.chrome === 'undefined' ||
        !globalThis.chrome.storage
      ) {
        resolve(false);
        return;
      }

      globalThis.chrome.storage.local.get(
        ['mobile_banner_dismissed'],
        result => {
          resolve(Boolean(result.mobile_banner_dismissed));
        }
      );
    });
  }

  // Persist the mobile app banner dismissal so it never reappears
  static async setMobileBannerDismissed() {
    return new Promise(resolve => {
      if (
        typeof globalThis.chrome === 'undefined' ||
        !globalThis.chrome.storage
      ) {
        resolve(false);
        return;
      }

      globalThis.chrome.storage.local.set(
        { mobile_banner_dismissed: true },
        () => {
          resolve(!globalThis.chrome.runtime.lastError);
        }
      );
    });
  }

  // Check if it's the first run
  static async isFirstRun() {
    return new Promise(resolve => {
      if (
        typeof globalThis.chrome === 'undefined' ||
        !globalThis.chrome.storage
      ) {
        resolve(true); // Assume first run if no storage available
        return;
      }

      globalThis.chrome.storage.local.get(['first_run_completed'], result => {
        resolve(!result.first_run_completed);
      });
    });
  }

  // Mark first run as completed
  static async setFirstRunCompleted() {
    return new Promise(resolve => {
      if (
        typeof globalThis.chrome === 'undefined' ||
        !globalThis.chrome.storage
      ) {
        resolve(false);
        return;
      }

      globalThis.chrome.storage.local.set({ first_run_completed: true }, () => {
        resolve(!globalThis.chrome.runtime.lastError);
      });
    });
  }

  // Clear all storage
  static async clearAll() {
    // Clear the in-memory cache
    storageCache.jwt_token = undefined;
    storageCache.user_id = undefined;
    storageCache.status = undefined;
    storageCache.last_used_group = undefined;
    storageCache.user_config = undefined;
    storageCache._initialized = false;

    return new Promise(resolve => {
      if (
        typeof globalThis.chrome === 'undefined' ||
        !globalThis.chrome.storage
      ) {
        resolve(false);
        return;
      }

      globalThis.chrome.storage.local.clear(() => {
        resolve(!globalThis.chrome.runtime.lastError);
      });
    });
  }
}
